<template>
  <div class="category">
    <div class="category-header">
      <h3 class="title">{{ title }}</h3>
      <span class="item-count">{{ items.length }}件</span>
    </div>
    <ul class="checklist">
      <li
        v-for="(item, index) in items"
        :key="`${item.PAGE_NO}-${item.CHECKLIST_NO}`"
        class="checklist-item"
        :class="{
          completed: item.COMPLETED_FLG === '1',
          overdue: isOverdue(item),
          upcoming: isUpcoming(item),
          'mobile-hidden': index >= mobileItemLimit && !isExpanded,
        }"
      >
        <!-- 選択用チェックボックス -->
        <div v-if="canWrite" class="checkbox-wrapper">
          <input
            type="checkbox"
            :id="`checklist-${item.PAGE_NO}-${item.CHECKLIST_NO}`"
            :checked="isItemSelected(item)"
            @change="selectItem(item)"
          />
          <label
            :for="`checklist-${item.PAGE_NO}-${item.CHECKLIST_NO}`"
          ></label>
        </div>

        <!-- チェックリスト詳細 + サブメンバー -->
        <div class="item-details">
          <div class="item-main">
            <span class="item-name">{{ item.ITEM_NAME }}</span>

            <!-- 非公開バッジ -->
            <span v-if="item.PRIVATE_FLG === '1'" class="private-badge">
              <IconSvg name="lock-solid-full" size="xs" />
              非公開
            </span>

            <!-- 優先度バッジ -->
            <span
              v-if="itemType === 'TODO' && item.PRIORITY_LEVEL"
              class="priority-badge"
              :class="priorityClass(item.PRIORITY_LEVEL)"
            >
              <span class="priority-dot"></span>
              {{ priorityLabel(item.PRIORITY_LEVEL) }}
            </span>
          </div>

          <p
            v-if="itemType === 'TODO' && item.MEMO_TEXT"
            class="item-memo"
          >
            {{ item.MEMO_TEXT }}
          </p>

          <div class="item-meta">
            <!-- 複数担当者（TODO） -->
            <span
              v-if="itemType === 'TODO' && item.MEMBERS?.length"
              class="meta-item assignee assignee-list"
            >
              <span
                v-for="member in item.MEMBERS"
                :key="member.ASSIGNEE_USER_CODE"
                class="assignee-person"
                :title="member.MEMBER_NAME"
              >
                <span class="meta-icon assignee-icon">
                  <div class="assignee-avatar">
                    <img
                      v-if="member.PROFILE_IMAGE"
                      :src="member.PROFILE_IMAGE"
                      :alt="member.MEMBER_NAME"
                      loading="lazy"
                      decoding="async"
                      class="avatar-img"
                    />
                    <IconSvg
                      v-else
                      name="user-solid-full"
                      size="xs"
                      class="avatar-placeholder"
                    />
                  </div>
                </span>
                <span class="assignee-name">{{ member.MEMBER_NAME }}</span>
              </span>
            </span>

            <!-- 後方互換の単一担当者 -->
            <span
              v-else-if="
                itemType === 'TODO' && item.ASSIGNED_MEMBER_NAME
              "
              class="meta-item assignee"
              :title="item.ASSIGNED_MEMBER_NAME"
            >
              <span class="meta-icon assignee-icon">
                <div class="assignee-avatar">
                  <img
                    v-if="item.ASSIGNED_MEMBER_PROFILE_IMAGE"
                    :src="item.ASSIGNED_MEMBER_PROFILE_IMAGE"
                    :alt="item.ASSIGNED_MEMBER_NAME"
                    loading="lazy"
                    decoding="async"
                    class="avatar-img"
                  />
                  <IconSvg
                    v-else
                    name="user-solid-full"
                    size="xs"
                    class="avatar-placeholder"
                  />
                </div>
              </span>
              <span class="assignee-name">{{
                item.ASSIGNED_MEMBER_NAME
              }}</span>
            </span>

            <!-- 期限 -->
            <span
              v-if="itemType === 'TODO' && item.DEADLINE"
              class="meta-item deadline"
              :class="deadlineClass(item)"
            >
              <span class="meta-icon">
                <IconSvg name="calendar-solid-full" size="xs" />
              </span>
              {{ formatDate(item.DEADLINE) }}
              <span v-if="isOverdue(item)" class="deadline-alert">
                <IconSvg name="triangle-exclamation-solid-full" size="xs" />
                期限切れ
              </span>
              <span v-else-if="isUpcoming(item)" class="deadline-alert"
                >⏰ まもなく（3日以内）</span
              >
            </span>

            <!-- 完了日 -->
            <span v-if="item.COMPLETED_YMD" class="meta-item completed-date">
              <span class="meta-icon">
                <IconSvg name="check-solid-full" size="xs" />
              </span>
              完了: {{ formatDate(item.COMPLETED_YMD) }}
            </span>

            <!-- 通知設定 -->
            <span
              v-if="itemType === 'TODO' && item.DEADLINE_NOTIFY_FLG === '1'"
              class="meta-item notify"
            >
              <span class="meta-icon">
                <IconSvg name="bell-solid-full" size="xs" />
              </span>
              通知ON
            </span>
          </div>

          <!-- サブメンバー（持ち物のみ） -->
          <div v-if="itemType === 'BELONGINGS'" class="item-members">
            <div
              v-for="member in item.MEMBERS || []"
              :key="member.ASSIGNEE_USER_CODE"
              class="sub-member"
              :class="{ 'sub-member-done': member.COMPLETED_FLG === '1' }"
            >
              <span class="sub-member-avatar">
                <img
                  v-if="member.PROFILE_IMAGE"
                  :src="member.PROFILE_IMAGE"
                  class="sub-avatar-img"
                  :alt="member.MEMBER_NAME"
                  loading="lazy"
                  decoding="async"
                />
                <span v-else class="sub-avatar-placeholder">{{
                  member.MEMBER_NAME?.charAt(0)
                }}</span>
              </span>
              <span
                :class="[
                  'sub-member-name',
                  { 'text-done': member.COMPLETED_FLG === '1' },
                ]"
                :title="member.MEMBER_NAME"
              >
                {{ truncateMemberName(member.MEMBER_NAME) }}
              </span>
              <button
                v-if="canWrite"
                class="member-done-btn"
                :class="[
                  member.COMPLETED_FLG === '1' ? 'member-done-btn--done' : '',
                  isMemberProcessing(item, member) ? 'is-processing' : '',
                ]"
                @click="toggleMember(item, member)"
                :disabled="isMemberProcessing(item, member)"
                :title="member.COMPLETED_FLG === '1' ? '未完了に戻す' : '完了にする'"
              >
                <template v-if="isMemberProcessing(item, member)">
                  処理中...
                </template>
                <template v-else-if="member.COMPLETED_FLG === '1'">
                  <IconSvg name="check-solid-full" size="xs" />
                  準備OK
                </template>
                <template v-else>
                  <IconSvg name="check-solid-full" size="xs" />
                  チェック
                </template>
              </button>
              <button
                v-if="canWrite"
                class="rm-member-btn"
                @click="removeMember(item, member)"
                title="削除"
              >
                ×
              </button>
            </div>
            <!-- メンバー追加 -->
            <div v-if="canWrite" class="add-member-wrap">
              <button
                class="add-member-btn"
                @click="toggleDropdown(item.CHECKLIST_NO)"
                :disabled="isAddingMember(item)"
              >
                {{ isAddingMember(item) ? "追加中..." : "+ メンバー追加" }}
              </button>
              <div
                v-if="openDropdown === item.CHECKLIST_NO"
                class="member-dropdown"
              >
                <button
                  v-for="sm in availableMembers(item)"
                  :key="sm.MEMBER_CODE"
                  class="dropdown-item"
                  @click="addMember(item, sm.MEMBER_CODE)"
                  :disabled="isAddMemberProcessing(item, sm.MEMBER_CODE)"
                >
                  {{
                    isAddMemberProcessing(item, sm.MEMBER_CODE)
                      ? "追加中..."
                      : sm.MEMBER_NAME
                  }}
                </button>
                <span
                  v-if="availableMembers(item).length === 0"
                  class="dropdown-empty"
                  >追加できるメンバーがいません</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- アクションボタン -->
        <div v-if="canWrite" class="item-actions" @click.stop>
          <button
            class="kebab-button"
            @click="toggleActionMenu(item)"
            aria-label="アイテムメニュー"
          >
            ⋮
          </button>
          <div
            v-if="openActionItemKey === itemKey(item)"
            class="action-menu"
          >
            <button class="action-menu-item" @click="runAction(toggleComplete, item)">
              <IconSvg name="check-solid-full" size="sm" />
              {{ item.COMPLETED_FLG === "1" ? "未完了に戻す" : "完了にする" }}
            </button>
            <button class="action-menu-item" @click="runAction(copyItem, item)">
              <IconSvg name="copy-solid-full" size="sm" />
              コピー
            </button>
            <button class="action-menu-item" @click="runAction(editItem, item)">
              <IconSvg name="pen-to-square-solid-full" size="sm" />
              編集
            </button>
            <button
              class="action-menu-item action-menu-delete"
              @click="runAction(deleteItem, item)"
            >
              <IconSvg name="trash-solid-full" size="sm" />
              削除
            </button>
          </div>
        </div>
      </li>
    </ul>
    <button
      v-if="items.length > mobileItemLimit"
      class="mobile-list-toggle"
      @click="isExpanded = !isExpanded"
    >
      {{
        isExpanded
          ? "表示を減らす"
          : `さらに${items.length - mobileItemLimit}件表示`
      }}
    </button>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";
import IconSvg from "@/components/IconSvg.vue";
import { usechecklistDetailMemberAccountStore } from "@/store/checklist-detail-member-store";

export default {
  name: "ChecklistCategory",
  components: {
    IconSvg,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    shioriId: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      default: null,
    },
    shioriMembers: {
      type: Array,
      default: () => [],
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    canWrite: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["toggle-complete", "edit", "delete", "reload", "copy", "select-item"],
  setup(props, { emit }) {
    const memberStore = usechecklistDetailMemberAccountStore();
    const openDropdown = ref(null);
    const openActionItemKey = ref(null);
    const isExpanded = ref(false);
    const processingMemberKeys = ref(new Set());
    const processingAddKeys = ref(new Set());
    const mobileItemLimit = 5;

    const itemKey = (item) => `${item.PAGE_NO}-${item.CHECKLIST_NO}`;
    const memberActionKey = (item, memberCode) =>
      `${itemKey(item)}-${memberCode}`;

    const updateProcessingSet = (stateRef, key, isProcessing) => {
      const next = new Set(stateRef.value);
      if (isProcessing) {
        next.add(key);
      } else {
        next.delete(key);
      }
      stateRef.value = next;
    };

    const isMemberProcessing = (item, member) =>
      processingMemberKeys.value.has(
        memberActionKey(item, member.ASSIGNEE_USER_CODE),
      );

    const isAddMemberProcessing = (item, memberCode) =>
      processingAddKeys.value.has(memberActionKey(item, memberCode));

    const isAddingMember = (item) =>
      Array.from(processingAddKeys.value).some((key) =>
        key.startsWith(`${itemKey(item)}-`),
      );

    const toggleActionMenu = (item) => {
      if (!props.canWrite) return;
      const key = itemKey(item);
      openActionItemKey.value =
        openActionItemKey.value === key ? null : key;
    };

    const runAction = (action, item) => {
      if (!props.canWrite) return;
      openActionItemKey.value = null;
      action(item);
    };

    const closeActionMenu = () => {
      openActionItemKey.value = null;
    };

    onMounted(() => {
      document.addEventListener("click", closeActionMenu);
    });

    onUnmounted(() => {
      document.removeEventListener("click", closeActionMenu);
    });

    const isItemSelected = (item) => {
      return props.selectedItems.includes(`${item.PAGE_NO}-${item.CHECKLIST_NO}`);
    };

    const selectItem = (item) => {
      if (!props.canWrite) return;
      emit("select-item", item);
    };

    const toggleComplete = (item) => {
      if (!props.canWrite) return;
      emit("toggle-complete", item);
    };

    const editItem = (item) => {
      if (!props.canWrite) return;
      emit("edit", item);
    };

    const deleteItem = (item) => {
      if (!props.canWrite) return;
      emit("delete", item);
    };

    const copyItem = (item) => {
      if (!props.canWrite) return;
      emit("copy", item);
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}/${String(date.getDate()).padStart(2, "0")}`;
    };

    const isOverdue = (item) => {
      if (props.itemType !== "TODO") return false;
      if (!item.DEADLINE || item.COMPLETED_FLG === "1") return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const deadline = new Date(item.DEADLINE);
      deadline.setHours(0, 0, 0, 0);
      return deadline < today;
    };

    const isUpcoming = (item) => {
      if (props.itemType !== "TODO") return false;
      if (!item.DEADLINE || item.COMPLETED_FLG === "1") return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const deadline = new Date(item.DEADLINE);
      deadline.setHours(0, 0, 0, 0);
      const threeDaysLater = new Date(today);
      threeDaysLater.setDate(threeDaysLater.getDate() + 3);
      return deadline >= today && deadline <= threeDaysLater;
    };

    const deadlineClass = (item) => {
      if (isOverdue(item)) return "overdue-deadline";
      if (isUpcoming(item)) return "upcoming-deadline";
      return "";
    };

    const priorityClass = (level) => {
      return `priority-${level?.toLowerCase() || "normal"}`;
    };

    const priorityLabel = (level) => {
      const labels = {
        HIGH: "高",
        NORMAL: "通常",
        LOW: "低",
      };
      return labels[level] || "通常";
    };

    const truncateMemberName = (name) => {
      const characters = Array.from(name || "");
      return characters.length > 5
        ? `${characters.slice(0, 5).join("")}...`
        : characters.join("");
    };

    const toggleDropdown = (checklistNo) => {
      if (!props.canWrite) return;
      openDropdown.value =
        openDropdown.value === checklistNo ? null : checklistNo;
    };

    const availableMembers = (item) => {
      const existingCodes = new Set(
        (item.MEMBERS || []).map((m) => m.ASSIGNEE_USER_CODE),
      );
      return props.shioriMembers.filter(
        (sm) => !existingCodes.has(sm.MEMBER_CODE),
      );
    };

    const addMember = async (item, memberCode) => {
      if (!props.canWrite) return;
      const key = memberActionKey(item, memberCode);
      if (processingAddKeys.value.has(key)) return;
      updateProcessingSet(processingAddKeys, key, true);
      try {
        await memberStore.addMember(
          props.shioriId,
          item.PAGE_NO,
          item.CHECKLIST_NO,
          memberCode,
        );
        const member = props.shioriMembers.find(
          (sm) => sm.MEMBER_CODE === memberCode,
        );
        item.MEMBERS = [
          ...(item.MEMBERS || []),
          {
            ASSIGNEE_USER_CODE: memberCode,
            MEMBER_NAME: member?.MEMBER_NAME || memberCode,
            PROFILE_IMAGE: member?.PROFILE_IMAGE_BASE64 || null,
            COMPLETED_FLG: "0",
            COMPLETED_YMD: null,
          },
        ];
        openDropdown.value = null;
      } catch (e) {
        console.error("メンバー追加失敗", e);
      } finally {
        updateProcessingSet(processingAddKeys, key, false);
      }
    };

    const toggleMember = async (item, member) => {
      if (!props.canWrite) return;
      const key = memberActionKey(item, member.ASSIGNEE_USER_CODE);
      if (processingMemberKeys.value.has(key)) return;
      updateProcessingSet(processingMemberKeys, key, true);
      try {
        const result = await memberStore.toggleMemberComplete(
          props.shioriId,
          item.PAGE_NO,
          item.CHECKLIST_NO,
          member.ASSIGNEE_USER_CODE,
        );
        const newFlg =
          result?.completed_flg || (member.COMPLETED_FLG === '1' ? '0' : '1');
        member.COMPLETED_FLG = newFlg;
        member.COMPLETED_YMD =
          newFlg === '1' ? new Date().toISOString().slice(0, 10) : null;

        if (newFlg === '1' && item.COMPLETED_FLG !== '1') {
          // このメンバー完了後に全員完了となるかチェック
          const allDone = (item.MEMBERS || []).every(m =>
            m.COMPLETED_FLG === '1'
          );
          if (allDone) {
            // 全員完了 → アイテム自体も完了にする
            emit('toggle-complete', item);
            return;
          }
        } else if (newFlg === '0' && item.COMPLETED_FLG === '1') {
          // メンバーが取り消し → アイテムも未完了に戻す
          emit('toggle-complete', item);
          return;
        }
      } catch (e) {
        console.error('完了トグル失敗', e);
      } finally {
        updateProcessingSet(processingMemberKeys, key, false);
      }
    };

    const removeMember = async (item, member) => {
      if (!props.canWrite) return;
      try {
        await memberStore.removeMember(
          props.shioriId,
          item.PAGE_NO,
          item.CHECKLIST_NO,
          member.ASSIGNEE_USER_CODE,
        );
        item.MEMBERS = (item.MEMBERS || []).filter(
          (m) => m.ASSIGNEE_USER_CODE !== member.ASSIGNEE_USER_CODE,
        );
      } catch (e) {
        console.error("メンバー削除失敗", e);
      }
    };

    return {
      toggleComplete,
      editItem,
      deleteItem,
      copyItem,
      formatDate,
      isOverdue,
      isUpcoming,
      deadlineClass,
      priorityClass,
      priorityLabel,
      truncateMemberName,
      openDropdown,
      toggleDropdown,
      availableMembers,
      addMember,
      toggleMember,
      isMemberProcessing,
      isAddMemberProcessing,
      isAddingMember,
      removeMember,
      isItemSelected,
      selectItem,
      isExpanded,
      mobileItemLimit,
      openActionItemKey,
      itemKey,
      toggleActionMenu,
      runAction,
    };
  },
};
</script>

<style scoped>
.category {
  background: white;
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-md);
  margin-bottom: 1rem;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid var(--color-border);
}

.title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
}

.item-count {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  background: var(--color-cool-bg);
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-full);
}

.checklist {
  list-style: none;
  padding: 0;
  margin: 0;
}

.mobile-list-toggle {
  display: none;
}

.checklist-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
  border: 1px solid transparent;
}

.checklist-item:hover {
  background-color: var(--color-background);
  border-color: var(--color-border);
}

.checklist-item.completed {
  opacity: 0.6;
}

.checklist-item.completed .item-name {
  text-decoration: line-through;
  color: var(--color-text-light);
}

.checklist-item.overdue {
  background-color: #fef2f2;
  border-color: #fecaca;
}

.checklist-item.upcoming {
  background-color: #fff7ed;
  border-color: #fed7aa;
}

/* チェックボックス */
.checkbox-wrapper {
  position: relative;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.checkbox-wrapper input[type="checkbox"] {
  appearance: none;
  width: 20px;
  height: 20px;
  border: 2px solid var(--color-border);
  border-radius: 4px;
  cursor: pointer;
  transition: all var(--transition-base);
}

.checkbox-wrapper input[type="checkbox"]:checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='white'%3E%3Cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3E%3C/svg%3E");
}

.checkbox-wrapper label {
  position: absolute;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  cursor: pointer;
}

/* アイテム詳細 */
.item-details {
  flex: 1;
  min-width: 0;
}

.item-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.item-name {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--color-text);
  overflow-wrap: anywhere;
  word-break: break-word;
}

.item-memo {
  margin: 0 0 0.55rem;
  padding: 0.5rem 0.65rem;
  border-left: 3px solid var(--primary-color);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
  background: var(--color-background);
  color: var(--color-text-muted);
  font-size: 0.8rem;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.priority-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.125rem 0.5rem;
  border-radius: var(--radius-full);
  white-space: nowrap;
}

.private-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: var(--radius-full);
  background-color: #fef9c3;
  color: #92400e;
  border: 1px solid #fde047;
  white-space: nowrap;
}

.priority-high {
  background-color: #fee2e2;
  color: #dc2626;
}

.priority-normal {
  background-color: #fef3c7;
  color: #d97706;
}

.priority-low {
  background-color: #d1fae5;
  color: #059669;
}

.item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.8125rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--color-text-muted);
}

.meta-icon {
  font-size: 0.875rem;
}

.assignee-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.assignee-avatar {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--color-cool-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.assignee-avatar .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.assignee-avatar .avatar-placeholder {
  color: var(--color-text-light);
}

.assignee {
  color: #3b82f6;
}

.assignee-list {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.assignee-person {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.deadline.overdue-deadline {
  color: #dc2626;
  font-weight: 500;
}

.deadline.upcoming-deadline {
  color: #d97706;
  font-weight: 500;
}

.deadline-alert {
  font-weight: 600;
  font-size: 0.75rem;
}

.completed-date {
  color: #059669;
}

.notify {
  color: var(--primary-color);
}

/* アクションボタン */
.item-actions {
  position: relative;
  flex-shrink: 0;
  align-self: flex-start;
}

.kebab-button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: white;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: all var(--transition-base);
}

.kebab-button:hover {
  background: var(--color-background);
  border-color: var(--color-border);
}

.action-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 50;
  min-width: 150px;
  overflow: hidden;
  background: white;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
}

.action-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  background: white;
  border: none;
  color: var(--color-text);
  font-size: 0.8125rem;
  text-align: left;
  cursor: pointer;
}

.action-menu-item:hover {
  background: var(--color-background);
}

.action-menu-delete {
  color: #dc2626;
}

@media (max-width: 640px) {
  .category {
    width: 100%;
    padding: 0.875rem;
    border-radius: var(--radius-md);
  }

  .category-header {
    margin-bottom: 0.625rem;
    padding-bottom: 0.5rem;
  }

  .checklist-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.625rem 0.375rem;
  }

  .checklist-item.mobile-hidden {
    display: none;
  }

  .checkbox-wrapper {
    flex-shrink: 0;
    width: 20px;
  }

  .item-details {
    flex: 1;
    min-width: 0;
  }

  .item-main {
    gap: 0.25rem;
    margin-bottom: 0.25rem;
  }

  .item-name {
    font-size: 0.875rem;
  }

  .priority-badge,
  .private-badge {
    padding: 0.1rem 0.375rem;
    font-size: 0.625rem;
  }

  .item-meta {
    gap: 0.25rem 0.5rem;
    font-size: 0.6875rem;
  }

  .assignee-list {
    flex-wrap: nowrap;
    gap: 0;
  }

  .assignee-person {
    margin-left: -3px;
  }

  .assignee-person:first-child {
    margin-left: 0;
  }

  .assignee-name {
    display: none;
  }

  .deadline-alert {
    font-size: 0.625rem;
  }

  .item-actions {
    flex-shrink: 0;
  }

  .kebab-button {
    width: 28px;
    height: 28px;
    font-size: 1.125rem;
  }

  .action-menu {
    position: fixed;
    right: 0.75rem;
    bottom: max(0.75rem, env(safe-area-inset-bottom));
    top: auto;
    left: 0.75rem;
    z-index: 1000;
    min-width: 0;
    border-radius: 16px;
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.25);
  }

  .action-menu-item {
    padding: 0.875rem 1rem;
    font-size: 0.875rem;
  }

  .mobile-list-toggle {
    display: block;
    width: 100%;
    margin-top: 0.5rem;
    padding: 0.5rem;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    background: var(--color-background);
    color: var(--color-text-muted);
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
  }
}

/* サブメンバー（持ち物） */
.item-members {
  width: 100%;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--color-border);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 0.4rem;
}

.sub-member {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.5rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-light);
  transition: background var(--transition-fast);
}

.sub-member:hover {
  background: var(--color-background);
}

.sub-member-check {
  appearance: none;
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-border);
  border-radius: 3px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--transition-fast);
}

.sub-member-check:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='white'%3E%3Cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3E%3C/svg%3E");
}

/* 完了ボタン（サブメンバー名横） */
.member-done-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--primary-color);
  background: white;
  color: var(--primary-color);
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.add-member-wrap {
  grid-column: 1 / -1;
}

@media (max-width: 640px) {
  .item-members {
    display: flex;
    flex-direction: column;
  }

  .member-done-btn {
    padding: 0.25rem 0.45rem;
  }
}

.member-done-btn:hover {
  background: var(--primary-color);
  color: white;
}

.member-done-btn:disabled,
.add-member-btn:disabled,
.dropdown-item:disabled {
  opacity: 0.65;
  cursor: wait;
}

.member-done-btn.is-processing {
  background: var(--color-cool-bg);
  border-color: var(--color-border);
  color: var(--color-text-muted);
}

.member-done-btn--done {
  background: #22c55e;
  border-color: #22c55e;
  color: white;
  text-decoration: none;
}

.member-done-btn--done:hover {
  background: #16a34a;
  border-color: #16a34a;
  color: white;
}

.sub-member-name {
  flex: 1;
  font-size: 0.85rem;
  color: var(--color-text);
}

/* アイテム完了ボタン（左から） */
.complete-btn {
  color: var(--color-text-muted);
  border-color: transparent;
  background: transparent;
}

.complete-btn:hover {
  color: var(--primary-color);
  background: var(--color-background);
}

.complete-btn--done {
  color: #22c55e;
}

.complete-btn--done:hover {
  color: #16a34a;
}

.sub-member-label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
  flex: 1;
  font-size: 0.85rem;
  color: var(--color-text);
}

.sub-member-done .sub-member-label .text-done {
  text-decoration: line-through;
  color: var(--color-text-light);
}

.sub-member-name.text-done {
  text-decoration: line-through;
  color: var(--color-text-light);
}

.sub-member-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--color-cool-bg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sub-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sub-avatar-placeholder {
  width: 100%;
  height: 100%;
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
}

.rm-member-btn {
  background: none;
  border: none;
  color: var(--color-border);
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 0.2rem;
  border-radius: 3px;
  transition: color var(--transition-fast);
  flex-shrink: 0;
}

.rm-member-btn:hover {
  color: #ef4444;
}

.add-member-wrap {
  position: relative;
  margin-top: 0.15rem;
}

.add-member-btn {
  background: none;
  border: 1px dashed var(--color-border);
  color: var(--color-text-muted);
  font-size: 0.78rem;
  padding: 0.2rem 0.75rem;
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: border-color var(--transition-fast), color var(--transition-fast);
}

.add-member-btn:hover {
  border-color: var(--primary-hover);
  color: var(--primary-hover);
}

.member-dropdown {
  position: absolute;
  left: 0;
  top: calc(100% + 4px);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  z-index: 50;
  min-width: 130px;
  padding: 0.25rem;
}

.dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0.4rem 0.6rem;
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  color: var(--color-text);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.dropdown-item:hover {
  background: var(--color-cool-bg);
}

.dropdown-empty {
  display: block;
  padding: 0.4rem 0.6rem;
  font-size: 0.8rem;
  color: var(--color-text-light);
}

@media (max-width: 640px) {
  .item-members {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-top: 0.375rem;
    padding-top: 0.375rem;
  }

  .sub-member {
    gap: 0.125rem;
    padding: 0.125rem;
    border: 1px solid var(--color-border-light);
  }

  .sub-member-avatar {
    width: 22px;
    height: 22px;
  }

  .sub-member-name {
    display: none;
  }

  .member-done-btn {
    position: relative;
    width: 22px;
    height: 22px;
    padding: 0;
    overflow: hidden;
    font-size: 0;
    background: white;
    border-color: var(--color-border);
    color: var(--color-text-light);
    justify-content: center;
  }

  .member-done-btn::after {
    content: "✓";
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 800;
    line-height: 1;
  }

  .member-done-btn--done::after {
    content: "✓";
  }

  .member-done-btn--done {
    background: #22c55e;
    border-color: #22c55e;
    color: white;
  }

  .member-done-btn :deep(.icon-svg) {
    display: none;
  }

  .member-done-btn.is-processing::after {
    content: "...";
  }

  .rm-member-btn {
    width: 18px;
    padding: 0;
    font-size: 0.875rem;
  }

  .add-member-wrap {
    display: flex;
    align-items: center;
    margin-top: 0;
  }

  .add-member-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
  }
}
</style>
